import { PurchaseOrderGet, Ulid } from "@kase/my-marathon-communication";
import { ReadonlySignal, computed, signal } from "@preact/signals";
import { ApiSliceParams } from "../interfaces";
import { createListController, getIdWithCompany } from "../utils";
import {
    PurchaseOrder,
    PurchaseOrderItemLine,
    PurchaseOrderLine,
    PurchaseOrderTextLine,
} from "./interfaces";

export const makePurchase = ({ connection, company }: ApiSliceParams) => {
    const ordersStore: Record<string, ReadonlySignal<PurchaseOrder[]>> = {};

    const open = async (order: PurchaseOrder, company: string) => {
        const con = connection.value;

        if (!con) {
            return;
        }

        order.loading.value = true;

        for await (const id of con.purchaseorder.lines.list({
            company,
            po: order._id,
        })) {
            for await (const item of con.purchaseorder.lines.get({
                company,
                id,
            })) {
                order.lines.value = [
                    ...order.lines.value,
                    {
                        ...item,
                        id: item.id.toString(),
                        _id: item.id,
                    },
                ];
            }
        }

        order.loading.value = false;
    };

    const view = (order: PurchaseOrderGet, cmp: string) => {
        const lines = signal<PurchaseOrderLine[]>([]);

        const finalOrder: PurchaseOrder = {
            ...order,
            id: getIdWithCompany(order.id, cmp),
            _id: order.id,
            loading: signal(false),
            company: cmp,
            lines,
            items: computed(() =>
                lines.value.filter(
                    (line): line is PurchaseOrderItemLine =>
                        line.line_type?.$case === "item",
                ),
            ),
            texts: computed(() =>
                lines.value.filter(
                    (line): line is PurchaseOrderTextLine =>
                        line.line_type?.$case === "text",
                ),
            ),
        };

        // @TODO: revisit this, should be opened manually
        //
        // open(finalOrder, cmp);
        void open;

        return finalOrder;
    };

    const {
        items: orders,
        allLoading: loading,
        allLoaded: loaded,
        listen: listenCompany,
    } = createListController<PurchaseOrderGet, PurchaseOrder, string>({
        name: "purchaseorder",
        connection,
        list: (con, company) => con.purchaseorder.items({ company }),
        getId: (order) => getIdWithCompany(order.id, company.value),
        getKey: (company) => company,
        view,
    });

    const listen = () => {
        listenCompany(company.value);
    };

    const projectOrders = (id: string) => {
        if (!(id in ordersStore)) {
            ordersStore[id] = computed(() =>
                orders.value.filter(
                    (order) => order.project?.project.toString() === id,
                ),
            );
        }

        if (!loading.peek() && !loaded.peek()) {
            listen();
        }

        return ordersStore[id];
    };

    const get = (
        id: string | Readonly<Ulid> | null | undefined,
        cmp = company.value,
    ) => {
        if (!id) {
            return null;
        }

        const key = getIdWithCompany(id, cmp);

        return orders.value.find((s) => s.id === key) ?? null;
    };

    return {
        orders,
        loading,
        projectOrders,
        listen,
        listenCompany,
        get,
    };
};
